import { Dispatch } from "redux";
import { setOpportunities, setOpportunitiesCount, setOpportunitiesSearchFields } from "./reducer";
import { toast } from "react-toastify";
import OpportunityFinance from "models/opportunity-finance";
import Opportunity, { IOpportunityQueryParam } from "models/opportunity";
import Employee from "models/employee";
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar";
import Branch from "models/branch";
import Client from "models/client";
import Department from "models/department";
import { getStageRank, getStageProbability } from "utils/opportunity-stage";

const computeOppForecastTotal = (opp: Opportunity): number => {
    if (!opp.opportunityFinances) return 0
    return opp.opportunityFinances.reduce((sum, f) => sum + Number(f.forecastAmount ?? 0), 0)
}

export const downloadOpportunityForecastCSV = (
    filteredOpportunities: Opportunity[],
    fiscalYear: number,
    query: IOpportunityQueryParam,
    branches: Branch[],
    projectManagers: Employee[],
    departments: Department[],
    clients: Client[]
) => {
    const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

    const branchNames = query.branchIds?.length
        ? query.branchIds.map(id => {
            const b = branches.find(b => b.id === id)
            return b ? b.name : id
        }).join(", ")
        : "All"
    const pmNames = query.ownerIds?.length
        ? query.ownerIds.map(id => {
            const pm = projectManagers.find(p => p.id === id)
            return pm ? `${pm.commonName ?? pm.firstName} ${pm.lastName}` : id
        }).join(", ")
        : "All"
    const disciplineNames = query.departmentIds?.length
        ? query.departmentIds.map(id => {
            const d = departments.find((d: any) => d.disciplineId === id)
            return d ? (d.description) : id
        }).join(", ")
        : "All"
    const clientNames = query.clientIds?.length
        ? query.clientIds.map(id => {
            const c = clients.find(c => c.id === id)
            return c ? c.name : id
        }).join(", ")
        : "All"
    const metadata = `Fiscal Year: ${fiscalYear} | Branch: ${branchNames} | Project Managers: ${pmNames} | Disciplines: ${disciplineNames} | Clients: ${clientNames}`

    const periods = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    const periodHeaders = periods.map(p => calendar.fiscalToLabel(fiscalYear, p))
    const headers = [
        "Stage", "Probability", "Opportunity", "Client",
        "Value", "Weighted Value",
        ...periodHeaders,
        "Forecast Total", "Forecast Weighted",
    ]

    const stageMap = new Map<string, { stageName: string; opps: Opportunity[] }>()
    for (const opp of filteredOpportunities) {
        const sid = opp.stage?.id ?? "unknown"
        if (!stageMap.has(sid)) {
            stageMap.set(sid, { stageName: opp.stage?.description ?? "Unknown", opps: [] })
        }
        stageMap.get(sid)!.opps.push(opp)
    }
    const sortedStages = Array.from(stageMap.values()).sort(
        (a, b) => getStageRank(a.stageName) - getStageRank(b.stageName)
    )

    const fmt = (n: number) => n.toFixed(2)
    const dataRows: string[][] = []
    let grandValue = 0, grandWeighted = 0, grandForecast = 0, grandForecastWeighted = 0
    const grandPeriods = new Array(12).fill(0)
    const grandWeightedPeriods = new Array(12).fill(0)

    for (const { stageName, opps } of sortedStages) {
        const prob = getStageProbability(stageName)
        let stageValue = 0, stageForecast = 0
        const stagePeriods = new Array(12).fill(0)
        const oppRows: string[][] = []

        for (const opp of opps) {
            const oppValue = Number(opp.amount ?? 0)
            const forecastTotal = computeOppForecastTotal(opp)
            const oppPeriodValues = periods.map(p => {
                const pF = opp.opportunityFinances?.find(f => f.fiscalPeriod === ((fiscalYear * 100) + p))
                return Number(pF?.forecastAmount ?? 0)
            })

            stageValue += oppValue
            stageForecast += forecastTotal
            oppPeriodValues.forEach((v, i) => stagePeriods[i] += v)

            oppRows.push([
                "", "",
                opp.description ?? "",
                (opp as any).client?.name ?? (opp as any).account?.name ?? "",
                fmt(oppValue), fmt(oppValue * prob),
                ...oppPeriodValues.map(fmt),
                fmt(forecastTotal), fmt(forecastTotal * prob),
            ])
        }

        const stageForecastWeighted = stageForecast * prob
        dataRows.push([
            stageName, `${Math.round(prob * 100)}%`, "", "",
            fmt(stageValue), fmt(stageValue * prob),
            ...stagePeriods.map(fmt),
            fmt(stageForecast), fmt(stageForecastWeighted),
        ])
        dataRows.push([
            "Weighted", "", "", "",
            "", "",
            ...stagePeriods.map(v => fmt(v * prob)),
            "", "",
        ])
        dataRows.push(...oppRows)

        grandValue += stageValue
        grandWeighted += stageValue * prob
        grandForecast += stageForecast
        grandForecastWeighted += stageForecastWeighted
        stagePeriods.forEach((v, i) => grandPeriods[i] += v)
        stagePeriods.forEach((v, i) => grandWeightedPeriods[i] += v * prob)
    }

    dataRows.push([
        "Grand Total", "", "", "",
        fmt(grandValue), fmt(grandWeighted),
        ...grandPeriods.map(fmt),
        fmt(grandForecast), fmt(grandForecastWeighted),
    ])

    dataRows.push([
        "Grand Total (Weighted)", "", "", "",
         "", "",
        ...grandWeightedPeriods.map(fmt),
         "", "",
    ])

    const csv = [[metadata], [], headers, ...dataRows]
        .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n")

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `opportunity-forecast-${fiscalYear}.csv`
    a.click()
    URL.revokeObjectURL(url)
}

export const getOpportunityForecast = (query?: IOpportunityQueryParam) => async (dispatch: Dispatch) => {
    try {
        let queryString = '';
        if (query) {
            const params = new URLSearchParams();

            Object.entries(query).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;

                if (Array.isArray(value)) {
                    value.forEach(v => {
                        if (v) params.append(key, v);
                    });
                } else {
                    params.append(key, String(value));
                }
            });

            queryString = params.toString();
        }

        const url = `${process.env.REACT_APP_API_URL}/opportunity/opportunity-finance${queryString ? `?${queryString}` : ''}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();

        dispatch(setOpportunities(data.data.data));
        dispatch(setOpportunitiesCount(data.data.count));

    } catch (err: any) {
        toast.error(err.message ?? "Error getting opportunity forecast");
    }
};

export const updateOpportunityFinance = async (
    opportunityFinances: OpportunityFinance[]
): Promise<OpportunityFinance[] | null> => {
    try {
        const payload = opportunityFinances.map(p => ({
            opportunityId: p.opportunityId,
            fiscalPeriod: p.fiscalPeriod,
            forecastAmount: Number(p.forecastAmount),
        }));

        const res = await fetch(
            `${process.env.REACT_APP_API_URL}/opportunity/opportunity-finance`,
            {
                credentials: "include",
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            }
        );

        if (!res.ok) {
            let errBody: any = null;
            try { errBody = await res.json(); } catch { /* ignore */ }
            throw new Error(errBody?.message ?? "Failed to update opportunity finance");
        }

        const data = await res.json();
        toast.success("Opportunity forecast has been updated");
        return data.data as OpportunityFinance[];

    } catch (err: any) {
        toast.error(err?.message ?? "Error updating opportunity finance");
        return null;
    }
};

export const getOpportunitySearchFields = () => async (dispatch: Dispatch) => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/opportunity/search-fields`;
        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        dispatch(setOpportunitiesSearchFields(data.data));
        return data.data;

    } catch (err: any) {
        toast.error(err.message ?? "Error getting opportunity search fields");
    }
};